import { ChangeDetectionStrategy, Component, computed, effect, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoModule } from '@jsverse/transloco';
import { produce } from 'immer';
import { LinkAttributeConverter } from 'shared/domain';
import {
  IconDirective,
  InputDirective,
  LabelDirective,
  SelectComponent,
  SelectImports,
  SelectOptionComponent,
  SpinnerComponent,
} from 'shared/ui-common';

import {
  FlowchartDecisionNodeDto,
  FlowchartLinkAttributeDto,
  FlowchartLinkAttributeOperator,
  FlowchartLinkDto,
  FlowchartLinkValueDto,
  FlowchartLinkValueType,
  ObservationPermittedDataType,
  RuleType,
} from '@treetop/shared/model';

import { ValueSetPickerComponent } from '../../../../../../../../../shared/ui-common/src/lib/components/value-set-picker/value-set-picker.component';
import { LinkValueInputType } from '../../../../../infrastructure/link-type-mappers/MapperTypes';
import { ObservationDataTypeMapper } from '../../../../../infrastructure/link-type-mappers/ObservationDataTypeMapper';
import { RuleTypeMapper } from '../../../../../infrastructure/link-type-mappers/RuleTypeMapper';

@Component({
  standalone: true,
  imports: [
    FormsModule,
    TranslocoModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    ValueSetPickerComponent,
    SpinnerComponent,
    SelectComponent,
    MatIconModule,
    SelectOptionComponent,
    SelectImports,
    LabelDirective,
    InputDirective,
    IconDirective,
  ],
  selector: 'sidebar-link-value',
  templateUrl: './link-value.component.html',
  styleUrls: ['./link-value.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkValueComponent {
  public link = input<FlowchartLinkDto>();
  public decisionRuleType = input<RuleType>();
  public decisionODType = input<ObservationPermittedDataType>();
  public loading = input(false);
  public sourceDecision = input<FlowchartDecisionNodeDto>();

  public linkValueChanged = output<Partial<FlowchartLinkDto>>();

  private mappedLinkValueType = computed(() => {
    const decisionRuleType = this.decisionRuleType();
    const decisionODType = this.decisionODType();
    const selectedValues = this.selectedValues();
    const sourceDecision = this.sourceDecision();

    let mappedLinkValueType: {
      selects: any;
      inputType: LinkValueInputType;
      payloadAttribute: string;
    } | null = null;

    if (decisionRuleType) {
      mappedLinkValueType = RuleTypeMapper.mapTypeToSelect(decisionRuleType);
    } else if (decisionODType !== null && decisionODType >= 0) {
      let isMultiSelect = false;
      let isOrdered = false;

      if (decisionODType === ObservationPermittedDataType.CodeableConcept) {
        const selected = selectedValues;
        isMultiSelect = selected ? Object.keys(selected).length >= 2 : false;

        isOrdered = sourceDecision?.guidelineObservation?.observationDefinition?.codeableConcept?.applyOrder ?? false;
      }

      mappedLinkValueType = ObservationDataTypeMapper.mapTypeToSelect(decisionODType, isMultiSelect, isOrdered);
    }

    return mappedLinkValueType;
  });

  protected inputType = computed(() => this.mappedLinkValueType().inputType);

  protected payloadAttribute = computed(() => {
    const decisionODType = this.decisionODType();
    const link = this.link();
    const mappedLinkValueType = this.mappedLinkValueType();

    const isRangeType = [
      ObservationPermittedDataType.Quantity,
      ObservationPermittedDataType.Integer,
      ObservationPermittedDataType.Ratio,
    ].includes(decisionODType);

    const isBetween = link?.attribute?.operator === FlowchartLinkAttributeOperator.Between;

    return isRangeType && isBetween ? FlowchartLinkValueType.numericalValueRange : mappedLinkValueType.payloadAttribute;
  });

  protected selects = computed(() => {
    return this.mappedLinkValueType().selects;
  });

  protected decisionUnit = computed(() =>
    this.sourceDecision()?.guidelineObservation?.unit?.name
      ? this.sourceDecision()?.guidelineObservation?.unit?.name
      : this.sourceDecision()?.guidelineObservation?.observationDefinition?.unit?.name,
  );

  protected linkValue = computed(() => this.link().attribute?.value);

  protected displayOperator = computed(() => LinkAttributeConverter.convertOperatorToValue(this.link()?.attribute?.operator));

  protected shouldShowButtons = computed(
    () => this.decisionRuleType() === this.RuleType.Boolean || this.decisionODType() === this.ObservationPermittedDataType.Boolean,
  );

  protected shouldShowInput = computed(() => !this.shouldShowButtons());

  private linkEntry = computed(() => Object.entries(this.link().attribute?.value ?? {}).find(([_, v]) => v !== null) ?? null);

  protected selectedValues = computed(() => {
    const entry = this.linkEntry();

    if (!entry) {
      return {};
    }

    const [type, value] = entry;

    return type === FlowchartLinkValueType.codeableConceptValueArray ? value : null;
  });

  protected displayValue = computed(() => {
    const entry = this.linkEntry();
    if (!entry) {
      return '';
    }

    const [type, value] = entry;

    if (type === FlowchartLinkValueType.codeableConceptValueArray) {
      return Object.values(value).join('; ');
    }

    if (type === FlowchartLinkValueType.numericalValueRange) {
      return value.item1;
    }

    return value ?? '';
  });

  protected displayValueTo = computed(() => {
    const entry = this.linkEntry();
    if (!entry) {
      return '';
    }

    const [type, value] = entry;

    return type === FlowchartLinkValueType.numericalValueRange ? value.item2 : '';
  });

  constructor() {
    effect(() => {
      // If the link attribute type is boolean but doesn't have a value yet, set it as `true`
      const link = this.link();
      const decisionRuleType = this.decisionRuleType();
      const decisionODType = this.decisionODType();

      const isBooleanRule = decisionRuleType === RuleType.Boolean || decisionODType === ObservationPermittedDataType.Boolean;
      const hasNoBooleanValue = link?.attribute?.value?.booleanValue == null;

      if (!isBooleanRule || !hasNoBooleanValue) {
        return;
      }

      const updatedLink = produce(link, (draft) => {
        draft.attribute ??= {} as FlowchartLinkAttributeDto;
        draft.attribute.value ??= {};

        draft.attribute.value.booleanValue = true;
      });

      this.linkValueChanged.emit(updatedLink);
    });
  }

  protected valueSetValuesSelected(values: Map<string, string>) {
    const isMultiSelect = values.size >= 2;

    const updatedLink = produce(this.link(), (draft) => {
      draft.attribute ??= {} as FlowchartLinkAttributeDto;
      draft.attribute.value ??= {};

      draft.attribute.value.codeableConceptValueArray = {
        ...Object.fromEntries(values),
      };

      if (isMultiSelect) {
        draft.attribute.operator = FlowchartLinkAttributeOperator.Equals;
      }
    });

    this.linkValueChanged.emit(updatedLink);
  }

  protected onOperatorChange(operator: string) {
    const oldIsBetween = this.link().attribute?.operator === FlowchartLinkAttributeOperator.Between;
    const newIsBetween = operator === FlowchartLinkAttributeOperator[FlowchartLinkAttributeOperator.Between];

    this.linkValueChanged.emit(
      produce(this.link(), (draft) => {
        draft.attribute ??= {} as FlowchartLinkAttributeDto;
        draft.attribute.value ??= {};

        if (newIsBetween !== oldIsBetween) {
          for (const key of Object.keys(draft.attribute.value)) {
            draft.attribute.value[key] = null;
          }
        }

        draft.attribute.operator = LinkAttributeConverter.convertValueToOperator(operator);
      }),
    );
  }

  protected onLinkToValueChange(value: number) {
    this.linkValueChanged.emit(
      produce(this.link(), (draft) => {
        draft.attribute ??= {} as FlowchartLinkAttributeDto;
        draft.attribute.value ??= {};

        draft.attribute.value.numericalValueRange = {
          ...draft.attribute?.value?.numericalValueRange,
          item2: value,
        };
      }),
    );
  }

  protected onLinkValueChange(value: string | number) {
    let newValue: FlowchartLinkValueDto;

    if (this.displayOperator() === FlowchartLinkAttributeOperator[FlowchartLinkAttributeOperator.Between]) {
      newValue = {
        [this.payloadAttribute()]: { item1: value, item2: this.link().attribute?.value?.numericalValueRange?.item2 || '' },
      };
    } else {
      newValue = {
        [this.payloadAttribute()]: value,
      };
    }

    this.linkValueChanged.emit(
      produce(this.link(), (draft) => {
        draft.attribute ??= { value: {} as any };

        draft.attribute.value = {
          ...draft.attribute.value,
          ...newValue,
        };
      }),
    );
  }

  protected onLinkButtonValueChange(choice: boolean) {
    this.linkValueChanged.emit(
      produce(this.link(), (draft) => {
        draft.attribute = {
          ...this.link().attribute,
          value: {
            [this.payloadAttribute()]: choice,
          },
        };
      }),
    );
  }

  protected FlowchartLinkAttributeOperator = FlowchartLinkAttributeOperator;
  protected RuleType = RuleType;
  protected ObservationPermittedDataType = ObservationPermittedDataType;
}
